import type { ApiKO, ApiOK, ApiResponse, ApiResponseError } from 'types/api';
import { logger } from './log';
import { getAppError, type TAppError } from './error';
import type { GetServerSidePropsContext } from 'next';
import { isObject, isString } from './assertions';
import { COOKIES, getCookie } from './helpers';
import { localeRegex } from './i18n';

const defaultError: TAppError = { code: 'unknown_error', message: 'unknown error' };
const API_GATEWAY = process.env.API_GATEWAY;
const currentDomain = process.env.INTEGRA_DOMAIN;

export function isDefaultError(error: ApiResponseError) {
  return error.code === defaultError.code;
}

export function isApiKO(data: ApiResponse<{}>): data is ApiKO {
  return data.status === 'KO';
}
export function getServerError(data?: ApiKO): TAppError {
  if (Array.isArray(data?.errors) && data?.errors?.length > 0) {
    return getAppError(data.errors[0]);
  }
  return defaultError;
}

export function checkForServerError(r: ApiResponse<{}>) {
  if (typeof r === 'object' && r !== null) {
    return 'status' in r && r.status === 'KO';
  }
  return false;
}
type HandleFetchResponseReturn<T> = Promise<{ data: T; error: null } | { data: null; error: TAppError }>;

export async function handleFetchResponse<T>(response: Response, url?: string): HandleFetchResponseReturn<T> {
  const isJson = response.headers.get('content-type')?.includes('application/json');

  if (!isJson) {
    return { data: null, error: { code: 'json_error', message: 'response was not json', url } };
  }
  if (!response.ok) {
    if (response.status === 401) {
      return { data: null, error: { code: 'unauthorized', message: 'unauthorized', url } };
    }
    let error = await response.json();
    error = getServerError(error);
    return { data: null, error: { ...error, url } };
  }
  let json = null;
  try {
    json = (await response.json()) as T;
  } catch (e) {
    return { data: null, error: { code: 'json_error', message: 'response was not json', url } };
  }
  if (checkForServerError(json as ApiResponse<{}>)) {
    return { data: null, error: { ...getServerError(json as ApiKO), url } };
  }
  return { data: json, error: null };
}

type CreateRequestUrlParams = {
  path: string;
  base?: string;
  query?: Record<string, string | number | boolean | string[]>;
  queryString?: string;
};

export function createRequestUrl({ path, base = API_GATEWAY, query, queryString }: CreateRequestUrlParams) {
  const url = new URL(base + path);
  if (queryString) {
    url.search = queryString;
  }
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === null || value === undefined) {
        return;
      }
      if (Array.isArray(value)) {
        value.forEach((v) => {
          url.searchParams.append(key, encodeURIComponent(v));
        });
        return;
      } else {
      }
      url.searchParams.append(key, encodeURIComponent(value));
    });
  }
  return url;
}

const defaultHeaders = {
  'Content-Type': 'application/json',
};
type SkippableHeaders = 'client-id' | 'jb-request-id' | 'user-agent' | 'client-url';
type CreateRequestHeadersArgs = {
  init: HeadersInit | undefined;
  context: GetServerSidePropsContext | undefined;
  skip: SkippableHeaders[] | undefined;
};

export function createRequestHeaders({ init = {}, context, skip = [] }: CreateRequestHeadersArgs): Headers {
  const headers = new Headers({ ...defaultHeaders, ...init });
  if (context) {
    if (isString(context?.req?.headers?.['jb-request-id'])) {
      headers.append('JB-REQUEST-ID', context.req.headers['jb-request-id']);
    }
    if (isString(context?.req?.headers?.['user-agent'])) {
      headers.append('User-Agent', context.req.headers['user-agent']);
    }
    if (isString(context?.req?.cookies?.[COOKIES.CLIENT])) {
      headers.append('Cookie', `${COOKIES.CLIENT}=${context.req.cookies[COOKIES.CLIENT]}`);
    }
    if (isString(context?.req?.headers?.['client-url']) && context.req.url) {
      if (localeRegex.test(context?.locale ?? '')) {
        headers.append('client-url', `${currentDomain}/${context.locale}${context.req.url}`);
      }
    }
  }
  if (typeof window !== 'undefined' && !skip.includes('client-id')) {
    const clientId = getCookie(COOKIES.CLIENT);
    if (clientId) {
      headers.append(COOKIES.CLIENT, clientId);
    }
  }
  return headers;
}

type TDefaultInit = RequestInit & {
  context?: GetServerSidePropsContext;
};

const apiOrigin = new URL(process.env.API_GATEWAY ?? 'https://www.camper.com').origin;

type TFetchConfig = {
  responseHeaders?: boolean;
};
export async function $fetch<T>(input: CreateRequestUrlParams, requestInit?: TDefaultInit): Promise<ApiOK<T>>;
export async function $fetch<T>(
  input: CreateRequestUrlParams,
  requestInit: TDefaultInit | undefined,
  config: TFetchConfig,
): Promise<{ data: ApiOK<T>; headers: Headers }>;
export async function $fetch<T>(input: CreateRequestUrlParams, requestInit: TDefaultInit = {}, config?: TFetchConfig) {
  const url = createRequestUrl(input);
  const skipHeaders: SkippableHeaders[] = [];
  if (url.origin !== apiOrigin) {
    skipHeaders.push('client-id');
  }
  try {
    const defaultRequestInit: TDefaultInit = isObject(requestInit) ? requestInit : {};
    const { context, next, ...defaultInit } = defaultRequestInit;
    const headers = createRequestHeaders({ init: defaultInit?.headers, context, skip: skipHeaders });
    const init = defaultInit ?? { method: 'GET' };
    const r = await fetch(url, {
      ...init,
      headers,
    });

    const response = await handleFetchResponse<T>(r, url.toString());

    if (response.error) {
      throw response.error;
    }

    if (config?.responseHeaders) {
      return {
        data: response.data,
        headers: r.headers,
      };
    }

    return response.data;
  } catch (error) {
    const err = getAppError(error);
    logger.error({
      error: err,
    });
    throw err;
  }
}

/*
 * @param {string | null} accept
 * @returns {{locale: string | null, language: string | null, country: string | null}}
 */
export function parseAcceptLanguageHeader(accept?: string | null) {
  if (accept === null || accept === undefined) {
    return {
      locale: null,
      language: null,
      country: null,
    };
  }
  const acceptString = accept.split(';')[0];

  const localeMatch = new RegExp(/([a-z]){2}-[A-Z]{2}?/).exec(acceptString ?? '');
  if (Array.isArray(localeMatch)) {
    const [language, country] = localeMatch[0].split('-');
    return {
      locale: localeMatch[0],
      language,
      country,
    };
  }
  // if we only find language in the accept string
  const languageMatch = new RegExp(/([a-z]){2}/).exec(acceptString ?? '');
  if (Array.isArray(languageMatch)) {
    return {
      locale: null,
      language: languageMatch[0],
      country: null,
    };
  }

  return {
    locale: null,
    language: null,
    country: null,
  };
}
